import { useState, useEffect } from 'react';
import {
  Stack,
  FormControl,
  FormLabel,
  HStack
} from '@chakra-ui/react';
import SearchBar from '../../SearchBar/SearchBar';
import StatusDropdown from '../../Dropdown/StatusDropdown/StatusDropdown';
import ArtistDropdown from '../../Artist/ArtistDropdown/ArtistDropdown';
import AdminUserDropdown from '../../User/AdminUserDropdown/AdminUserDropdown';
import { ProcessStatus } from 'models/types';
import { ArtistModel, UserModel } from 'models';

export interface GroupingFiltersFormProps {
  defaultStatus?: ProcessStatus;
  defaultQuery?: string;
  defaultArtists?: ArtistModel[];
  defaultCreatedByUsers?: UserModel[];
  availableArtists?: ArtistModel[];
  availableAdminUsers?: UserModel[];
  onStatusChange: (status: ProcessStatus) => void;
  onQueryChange: (query: string) => void;
  onArtistChange: (artists: ArtistModel[]) => void;
  onCreatedByChange: (users: UserModel[]) => void;
}

export default function GroupingFiltersForm({
  defaultStatus = ProcessStatus.PENDING,
  defaultQuery = '',
  defaultArtists = [],
  defaultCreatedByUsers = [],
  availableArtists = [],
  availableAdminUsers = [],
  onStatusChange,
  onQueryChange,
  onArtistChange,
  onCreatedByChange
}: GroupingFiltersFormProps) {
  const [selectedStatus, setSelectedStatus] = useState<ProcessStatus>(defaultStatus);
  const [query, setQuery] = useState<string>(defaultQuery);
  const [selectedArtists, setSelectedArtists] = useState<ArtistModel[]>(defaultArtists);
  const [selectedCreatedByUsers, setSelectedCreatedByUsers] = useState<UserModel[]>(defaultCreatedByUsers);

  // Sync internal state with prop changes
  useEffect(() => {
    setSelectedStatus(defaultStatus);
  }, [defaultStatus]);

  useEffect(() => {
    setQuery(defaultQuery);
  }, [defaultQuery]);

  useEffect(() => {
    setSelectedArtists(defaultArtists);
  }, [defaultArtists]);

  useEffect(() => {
    setSelectedCreatedByUsers(defaultCreatedByUsers);
  }, [defaultCreatedByUsers]);

  const handleStatusChange = (status: ProcessStatus) => {
    setSelectedStatus(status);
    onStatusChange(status);
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    onQueryChange(value);
  };

  const handleArtistChange = (artists: ArtistModel[]) => {
    setSelectedArtists(artists);
    onArtistChange(artists);
  };

  const handleCreatedByChange = (users: UserModel[]) => {
    setSelectedCreatedByUsers(users);
    onCreatedByChange(users);
  };

  return (
    <Stack spacing={4}>
      <HStack spacing={4} alignItems="flex-end">
        <FormControl flex={2}>
          <SearchBar
            onChange={handleQueryChange}
            defaultValue={defaultQuery}
          />
        </FormControl>
        <FormControl flex={1}>
          <FormLabel fontSize="sm" htmlFor="grouping-artist">Artist</FormLabel>
          <ArtistDropdown
            artists={availableArtists}
            onArtistSelect={handleArtistChange}
            defaultSelectedArtists={selectedArtists}
            multiSelectable={true}
            maxWidth={true}
          />
        </FormControl>
        <FormControl flex={1}>
          <FormLabel fontSize="sm" htmlFor="grouping-created-by">Created By</FormLabel>
          <AdminUserDropdown
            users={availableAdminUsers}
            onUserSelect={handleCreatedByChange}
            defaultSelectedUsers={selectedCreatedByUsers}
            multiSelectable={true}
            maxWidth={true}
          />
        </FormControl>
        <FormControl flex={1}>
          <FormLabel fontSize="sm" htmlFor="grouping-status">Status</FormLabel>
          <StatusDropdown
            value={selectedStatus}
            onChange={handleStatusChange}
            size="md"
          />
        </FormControl>
      </HStack>
    </Stack>
  );
}
